import { Button, Divider, Input } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { BiMinus, BiPlus } from 'react-icons/bi';
import { IoCartOutline } from 'react-icons/io5';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import { TbCircleCheckFilled } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import Box from 'src/components/Box';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { RootState } from 'src/store';
import CheckoutModal from './FormModal';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import ENVIRONMENT from 'src/utils/environments';
import WizardUpgradeModal from './WizardUpgradeModal';
import RequestCard from 'src/components/Card/RequestCard';
import { formatCurrency } from 'src/utils/string';

const stripePromise = loadStripe(ENVIRONMENT.STRIPE_PUBLISHABLE_KEY);

type HoursOptions = {
  hour_price: number;
  latest_refill: number;
  options: number[];
};

type HoursEstimate = {
  id: number;
  name: string;
  imageUrl: string;
  estimated_time: {
    min: number;
    max: number;
  };
  checkout: boolean;
};

type CartData = {
  items: {
    itemId: number;
    quantity: number;
  }[];
  totalMin: number;
  totalMax: number;
};

export type CheckoutData = {
  base_price: number;
  amount: number;
  client_secret: string;
  currency: string;
  qty: number;
  project_id: string;
  id: string;
};

const CheckoutPage: FC = () => {
  const userSelectedHours = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);

  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [cartData, setCartData] = useState<CartData | null>(null);
  const [selectedHours, setSelectedHours] = useState<number | null>(null);
  const [hoursOptions, setHoursOptions] = useState<HoursOptions | null>(null);

  const projectRetainer = useSelector(
    (globalState: RootState) => globalState.projectRetainer
  );

  const requestForms = useSelector(
    (state: RootState) => state.resources.requestForms
  );

  const recommendedOption =
    hoursOptions && cartData && cartData.items.length > 0
      ? hoursOptions.options.find((h) => h > cartData.totalMax)
      : null;

  const optionsEstimate: HoursEstimate[] = requestForms
    .filter(
      (
        item
      ): item is typeof item & {
        estimated_time: { min: number; max: number };
      } => item.checkout === true
    )
    .map((item) => ({
      id: item.id,
      name: item.name,
      imageUrl: item.imageUrl,
      estimated_time: {
        min: item.estimated_time.min / 60,
        max: item.estimated_time.max / 60,
      },
      card: item.card,
      groups: item.groups,
      icon: item.icon,
      checkout: item.checkout,
      tags: item.deliverables,
    }));

  const cartItemsDetailed = cartData
    ? cartData.items.map((item) => {
        const hourItem = optionsEstimate.find((h) => h.id === item.itemId);
        return {
          ...item,
          ...hourItem,
        };
      })
    : [];

  const getCheckoutInfo = async (projectUuid: string) => {
    const response = await apiRequests.get(
      `${apiRoutes.CHECKOUT_INFO}?project_uuid=${projectUuid}`
    );

    setHoursOptions({
      ...response.data.data,
      options: response.data.data.options.map((opt: number) => opt / 60),
    });
  };

  const toggleCart = (item: HoursEstimate) => {
    setCartData((previous) => {
      const prev = previous ?? {
        items: [],
        totalMin: 0,
        totalMax: 0,
      };

      const alreadyOnCart = prev.items.find((opt) => opt.itemId === item.id);

      let updatedItems;
      if (alreadyOnCart) {
        updatedItems = prev.items.map((opt) =>
          opt.itemId === item.id ? { ...opt, quantity: opt.quantity + 1 } : opt
        );
      } else {
        updatedItems = [...prev.items, { itemId: item.id, quantity: 1 }];
      }

      return {
        items: updatedItems,
        totalMin: prev.totalMin + item.estimated_time.min,
        totalMax: prev.totalMax + item.estimated_time.max,
      };
    });
  };

  const incrementItem = (itemId: number) => {
    const item = optionsEstimate.find((i) => i.id === itemId);
    if (!item) return;

    setCartData((prev) => {
      if (!prev) return null;

      const updatedItems = prev.items.map((opt) =>
        opt.itemId === itemId ? { ...opt, quantity: opt.quantity + 1 } : opt
      );

      return {
        items: updatedItems,
        totalMin: prev.totalMin + item.estimated_time.min,
        totalMax: prev.totalMax + item.estimated_time.max,
      };
    });
  };

  const decrementItem = (itemId: number) => {
    const item = optionsEstimate.find((i) => i.id === itemId);
    if (!item) return;

    setCartData((prev) => {
      if (!prev) return null;

      const existingItem = prev.items.find((i) => i.itemId === itemId);
      if (!existingItem) return prev;

      let updatedItems;
      if (existingItem.quantity === 1) {
        updatedItems = prev.items.filter((i) => i.itemId !== itemId);
      } else {
        updatedItems = prev.items.map((i) =>
          i.itemId === itemId ? { ...i, quantity: i.quantity - 1 } : i
        );
      }

      return {
        items: updatedItems,
        totalMin: prev.totalMin - item.estimated_time.min,
        totalMax: prev.totalMax - item.estimated_time.max,
      };
    });
  };

  const removeItem = (itemId: number) => {
    const item = optionsEstimate.find((i) => i.id === itemId);
    if (!item) return;

    setCartData((prev) => {
      if (!prev) return null;

      const existing = prev.items.find((i) => i.itemId === itemId);
      if (!existing) return prev;

      return {
        items: prev.items.filter((i) => i.itemId !== itemId),
        totalMin: prev.totalMin - item.estimated_time.min * existing.quantity,
        totalMax: prev.totalMax - item.estimated_time.max * existing.quantity,
      };
    });
  };

  const setupPayment = async () => {
    setIsLoading(true);

    try {
      const response = await apiRequests.post(`${apiRoutes.CHECKOUT_SETUP}`, {
        qty: selectedHours,
        project_uuid: projectRetainer.data?.uuid,
      });

      setCheckoutData({
        ...response.data.data,
        qty: selectedHours,
        project_id: projectRetainer.data?.uuid,
      });

      setIsCheckoutModalOpen(true);
    } catch (error) {
      console.error('Payment setup error:', error);
      alert('Failed to setup payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => setupPayment();

  const closeModal = (clearCart: boolean = false) => {
    setIsCheckoutModalOpen(false);
    setIsUpgradeModalOpen(false);

    if (clearCart) {
      setCartData(null);
      setSelectedHours(null);
      userSelectedHours.current = false;
    }
  };

  useEffect(() => {
    if (projectRetainer && projectRetainer.data) {
      getCheckoutInfo(projectRetainer.data.uuid);
    }
  }, [projectRetainer]);

  useEffect(() => {
    if (
      !userSelectedHours.current &&
      recommendedOption &&
      cartData &&
      cartData.items.length > 0
    ) {
      setSelectedHours(recommendedOption);
    }
  }, [recommendedOption, cartData]);

  useEffect(() => {
    if (
      hoursOptions &&
      cartData &&
      cartData.totalMax > Math.max(...hoursOptions.options)
    ) {
      setSelectedHours(null);
      userSelectedHours.current = false;
    }
  }, [cartData, hoursOptions]);

  return (
    <>
      <div className="mt-1 grid grid-cols-1 items-start gap-6 overflow-visible md:grid-cols-[1fr_min-content]">
        <div className="flex flex-col gap-6">
          <Box>
            <div className="mb-6 flex items-center gap-2">
              <IoCartOutline className="min-w-[20px] text-xl text-blue-600" />

              <h1 className="text-lg font-medium text-blue-700 ">
                Select the package of hours you need
              </h1>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6">
              {hoursOptions ? (
                hoursOptions.options.length > 0 ? (
                  hoursOptions.options.map((option, index) => {
                    const isSelected = selectedHours === option;
                    return (
                      <div
                        key={`${option}-${index}`}
                        onClick={() => {
                          userSelectedHours.current = true;
                          setSelectedHours((prev) =>
                            prev === option ? null : option
                          );
                        }}
                        className={`group relative flex aspect-square min-w-[130px] max-w-[130px] flex-1 basis-1/4 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-3xl border transition-colors duration-300
              ${
                isSelected
                  ? 'border-blue-600 bg-blue-100'
                  : 'border-transparent bg-[#F1F3F8] hover:cursor-pointer hover:border-blue-500 hover:bg-blue-100'
              }`}
                      >
                        <div className="flex flex-col items-center justify-center gap-1 transition-transform duration-300 group-hover:-translate-y-4">
                          <img
                            className="h-[40px] w-[40px]"
                            src={`/assets/checkout/clock-${index + 1}.png`}
                            alt="Clock"
                          />
                          <span className="text-lg font-bold">+{option}h</span>
                        </div>

                        <div className="absolute bottom-3 text-sm font-medium text-blue-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          Buy
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <p className="w-full pb-1 pt-6 text-center text-blue-700">
                      No hours available. Contact an administrator.
                    </p>

                    <div
                      onClick={() => setIsUpgradeModalOpen(true)}
                      className="flex aspect-square min-w-[130px] max-w-[130px] flex-1 basis-1/4 cursor-pointer flex-col items-center justify-center gap-1 rounded-3xl border border-transparent bg-[#F1F3F8] text-center transition-colors duration-300 hover:border-blue-500 hover:bg-blue-100 md:min-w-[130px] md:max-w-[130px]"
                    >
                      <h2 className="font-bold">Need more?</h2>
                      <p>Upgrade or downgrade</p>
                    </div>
                  </>
                )
              ) : (
                <div className="aspect-square min-w-[70px] max-w-[70px] flex-1 basis-1/4 rounded-3xl border bg-[#F1F3F8] md:min-w-[130px] md:max-w-[130px]" />
              )}

              {hoursOptions && hoursOptions.options.length > 0 && (
                <div
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="flex aspect-square min-w-[130px] max-w-[130px] flex-1 basis-1/4 cursor-pointer flex-col items-center justify-center gap-1 rounded-3xl border border-transparent bg-[#F1F3F8] text-center transition-colors duration-300 hover:border-blue-500 hover:bg-blue-100 md:min-w-[130px] md:max-w-[130px]"
                >
                  <h2 className="font-bold">Need more?</h2>
                  <p>Upgrade or Downgrade</p>
                </div>
              )}
            </div>
          </Box>
          <Box style={{ padding: 0, backgroundColor: 'transparent' }}>
            <h1 className="flex items-center gap-2 text-xl font-medium text-blue-700">
              <IoCartOutline className="text-xl text-blue-600" />
              Hours estimator
            </h1>
            <p>Select any item to estimate how many hours you need.</p>

            <div className="mt-6 grid auto-rows-[minmax(370px,auto)] grid-cols-1 items-stretch gap-8 min-[970px]:grid-cols-2 min-[1200px]:grid-cols-3 min-[1280px]:grid-cols-2 min-[1460px]:grid-cols-2 min-[1900px]:grid-cols-3">
              {optionsEstimate.map((option) => (
                <RequestCard
                  key={option.id}
                  request={option}
                  tagFormat="bullet-list"
                  renderButton={() => {
                    return (
                      <button
                        type="button"
                        onClick={() => toggleCart(option)}
                        className="flex items-center justify-center gap-[0.35rem]"
                      >
                        <span className="text-base font-semibold leading-none">
                          Add to cart
                        </span>
                        <span className="text-sm leading-none">
                          {option.estimated_time.min}-
                          {option.estimated_time.max} hours
                        </span>
                      </button>
                    );
                  }}
                />
              ))}
            </div>
          </Box>
        </div>
        <Box className="sticky top-[5.3rem] w-full space-y-6 bg-white md:min-w-[360px] md:max-w-[360px]">
          <h1 className="text-2xl font-bold text-blue-700">Shopping cart</h1>
          {(cartData && cartItemsDetailed.length > 0) || selectedHours ? (
            <>
              {cartData && cartItemsDetailed.length > 0 && (
                <>
                  <div className="space-y-4">
                    {cartItemsDetailed.map((item) => {
                      return (
                        <div
                          key={item.itemId}
                          className="flex items-center gap-4"
                        >
                          <img
                            src={`${ENVIRONMENT.CDN_ASSETS_URL}/requests/main-stg/${item.imageUrl}`}
                            alt={item.name}
                            className="aspect-square max-w-[50px] object-contain"
                          />
                          <div className="flex flex-1 flex-col gap-2">
                            <div className="flex w-full items-center justify-between">
                              <h2 className="font-medium">{item.name}</h2>
                              <span className="text-xs text-gray-400">
                                {item.estimated_time?.min || 0}-
                                {item.estimated_time?.max || 0}
                              </span>
                            </div>
                            <div className="flex w-full items-center justify-between">
                              <Button
                                onClick={() => removeItem(item.itemId)}
                                type="ghost"
                                className="!px-1"
                              >
                                <MdOutlineDeleteOutline />
                                <span className="ml-1 text-sm">Remove</span>
                              </Button>

                              <div className="flex items-center justify-center overflow-hidden rounded-lg border">
                                <button
                                  type="button"
                                  onClick={() => decrementItem(item.itemId)}
                                  className="flex !h-[32px] !w-[32px] items-center justify-center"
                                >
                                  <BiMinus />
                                </button>
                                <Input
                                  className="!h-[32px] !w-[36px] !border-none"
                                  value={item.quantity}
                                />
                                <button
                                  type="button"
                                  onClick={() => incrementItem(item.itemId)}
                                  className="flex !h-[32px] !w-[32px] items-center justify-center"
                                >
                                  <BiPlus />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Divider />

                  <div className="flex items-center justify-between">
                    <h1 className="text-xl font-medium">
                      Estimated total hours
                    </h1>
                    <span className="text-xl font-medium">
                      {cartData.totalMin}-{cartData.totalMax} hours
                    </span>
                  </div>

                  <Divider />
                </>
              )}

              <div>
                {hoursOptions &&
                cartData &&
                cartData.totalMax > Math.max(...hoursOptions.options) ? (
                  <div className="flex flex-col items-center gap-6 py-4 text-center">
                    <p className="text-lg font-medium text-blue-700">
                      With the hours you need, upgrading this month gives you
                      the best value.
                    </p>
                  </div>
                ) : (
                  <>
                    {cartData && cartItemsDetailed.length > 0 && (
                      <h1 className="text-lg font-medium text-blue-700">
                        Select Your Package
                      </h1>
                    )}
                    {hoursOptions &&
                    (selectedHours ||
                      (cartData && cartData.items.length > 0)) ? (
                      <div className="mt-4 flex flex-col">
                        {(() => {
                          const options: number[] = [];

                          const recommended =
                            cartData && cartData?.items.length > 0
                              ? hoursOptions.options.find(
                                  (h) => cartData && h > cartData.totalMax
                                )
                              : undefined;
                          if (recommended) options.push(recommended);

                          const secondary = hoursOptions.options.find(
                            (h) =>
                              cartData &&
                              h >= cartData.totalMin &&
                              h <= cartData.totalMax &&
                              h !== recommended
                          );

                          if (secondary) options.push(secondary);

                          if (
                            selectedHours &&
                            !options.includes(selectedHours)
                          ) {
                            options.push(selectedHours);
                          }

                          return options.map((option) => {
                            const isSelected = selectedHours === option;
                            const isRecommended = option === recommended;

                            return (
                              <div
                                key={option}
                                onClick={() => {
                                  userSelectedHours.current = true;
                                  setSelectedHours(option);
                                }}
                                className={`flex cursor-pointer items-center justify-between px-2 py-4 transition-colors ${
                                  isSelected ? 'bg-[#edf5ff]' : ''
                                }`}
                              >
                                <TbCircleCheckFilled
                                  size={32}
                                  className={`text-blue-600 ${
                                    isSelected ? '' : 'opacity-50'
                                  }`}
                                />

                                <div className="flex items-center gap-1 font-medium">
                                  <p>{option} hours</p>
                                  {isRecommended && <span>(Recommended)</span>}
                                </div>

                                <p className="text-lg font-semibold">
                                  {formatCurrency(
                                    (option * hoursOptions.hour_price) / 100
                                  )}
                                </p>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className="mt-6 p-4 text-center text-blue-700">
                        Select an option or add items to cart
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex h-80 items-center justify-center">
              <p className="text-blue-700">Your Lionshare cart is empty</p>
            </div>
          )}

          {cartData &&
          cartData?.totalMax > Math.max(...(hoursOptions?.options ?? [])) ? (
            <>
              <Button
                type="primary"
                onClick={() => setIsUpgradeModalOpen(true)}
                className="!h-10 w-full !border-[#52c41a] !bg-[#52c41a] !text-white 
                       hover:!border-[#389e0d] hover:!bg-[#389e0d]"
              >
                Upgrade my plan
              </Button>
              <p className="text-center">You can downgrade at any time.</p>
            </>
          ) : (
            <Button
              type="primary"
              disabled={!selectedHours}
              loading={isLoading}
              onClick={openModal}
              className={`
                  !h-10 
                  w-full 
                  !border-[#52c41a] 
                  !bg-[#52c41a]
                  !text-white 
                  hover:!border-[#389e0d]
                  hover:!bg-[#389e0d]
                  ${selectedHours ? '' : 'opacity-50'}
                `}
            >
              Buy now
            </Button>
          )}

          <div className="flex items-center justify-center gap-4">
            <img
              className="w-8"
              src="/assets/checkout/DinersClub.svg"
              alt="DinersClub"
            />
            <img className="w-8" src="/assets/checkout/Amex.svg" alt="Amex" />
            <img
              className="w-8"
              src="/assets/checkout/Mastercard.svg"
              alt="Mastercard"
            />
            <img
              className="w-8"
              src="/assets/checkout/Stripe.svg"
              alt="Stripe"
            />
            <img
              className="w-8"
              src="/assets/checkout/PayPal.svg"
              alt="PayPal"
            />
          </div>

          <p className="text-center text-xs text-gray-400">
            By confirming your payment, you allow Savage Global Marketing to
            charge you for this payment in accordance with their terms.
          </p>
        </Box>
      </div>

      <Elements stripe={stripePromise}>
        <CheckoutModal
          isOpen={isCheckoutModalOpen}
          onClose={closeModal}
          checkoutData={checkoutData as CheckoutData}
          hours={selectedHours as number}
        />
      </Elements>

      <WizardUpgradeModal
        projectUuid={projectRetainer.data?.uuid as string}
        isOpen={isUpgradeModalOpen}
        onClose={closeModal}
        latestRefill={hoursOptions?.latest_refill ?? null}
      />
    </>
  );
};

export default CheckoutPage;
